<template>
  <div class="bill-warranty-history col-12">
    <p class="text-warranty-history">Lịch sử hóa đơn</p>
    <div v-if="isLoading" class="text-center py-4">
      <b-spinner variant="primary" label="Đang tải..." />
    </div>

    <div v-else-if="errorMessage" class="text-center py-4">
      <span class="text-danger">{{ errorMessage }}</span>
    </div>
    <div
      v-if="!isLoading && !errorMessage"
      class="timeline-wrapper"
      @mouseleave="isHoveringAny = false"
    >
      <div
        v-for="(bill, index) in sortedBillChain"
        :key="bill.id"
        class="timeline-item"
        :class="{ 'is-current': bill.isCurrentBill }"
        @mouseenter="isHoveringAny = true"
        @mouseleave="isHoveringAny = false"
      >
        <div class="timeline-dot-wrapper">
          <div
            class="timeline-dot"
            :class="[
              bill.isCurrentBill ? 'dot-active' : 'dot-inactive',
              { 'dot-default': bill.isCurrentBill && !isHoveringAny },
            ]"
          />
          <div
            v-if="index < sortedBillChain.length - 1"
            class="timeline-line"
          />
        </div>
        <div
          class="timeline-card"
          :class="{
            'card-current': bill.isCurrentBill,
            'card-default': bill.isCurrentBill && !isHoveringAny,
          }"
          @click="onClickBill(bill)"
        >
          <div class="card-header-row">
            <div class="bill-type-label">
              <span class="bill-type-short">{{
                getBillTypeName(bill.type)
              }}</span>
              <span class="bill-number">ID: {{ bill.billNumber }}</span>
            </div>
            <div class="card-header-right">
              <span
                v-if="bill.status !== null && bill.status !== undefined"
                :class="getStatusClass(bill.status)"
              >
                {{ getBillStatusName(bill.status) }}
              </span>
              <span v-if="bill.isCurrentBill" class="badge-current ml-1">
                Hiện tại
              </span>
            </div>
          </div>
          <div class="card-meta">
            <span>Ngày tạo: {{ formatDate(bill.createdAt, CLIENT_DATE_TIME_NO_SECONDS ) }}</span>
            <span class="meta-separator">•</span>
            <span>{{ bill.storeName || bill.storeShortName || '--' }}</span>
          </div>
          <div v-if="bill.warrantyReasonName" class="card-reason">
            Lý do: {{ bill.warrantyReasonName }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { makeToastFaile } from '@/utils/common';
import { BILL_TYPE_HISTORY_LABEL } from '@/utils/constants';
import { CLIENT_DATE_TIME_NO_SECONDS, formatDate } from '@/utils/date';
import { BILL_TYPE, WARRANTY_BILL_STATUS } from '@/utils/enum';
import * as ENUM_NAME from '@/utils/enum-name';

export default {
  name: 'BillWarrantyHistory',
  props: {
    billId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
      isHoveringAny: false,
      CLIENT_DATE_TIME_NO_SECONDS,
      chainData: {
        billChain: [],
      },
    };
  },
  computed: {
    sortedBillChain() {
      return this.chainData.billChain || [];
    },
  },
  created() {
    this.fetchChainHistory();
  },
  methods: {
    formatDate,
    fetchChainHistory() {
      this.isLoading = true;
      this.errorMessage = '';

      ApiService.get(`bills/${this.billId}/bill-warranty-history`)
        .then(({ data }) => {
          const result = data.data;
          this.chainData = {
            billChain: result.billWarrantyHistory,
          };
        })
        .catch((error) => {
          this.errorMessage = 'Không thể tải lịch sử hóa đơn.';
          makeToastFaile(
            error.response ? error.response.data.message : error.message,
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getBillTypeName(type) {
      return BILL_TYPE_HISTORY_LABEL[type] || '--';
    },

    getBillStatusName(status) {
      return ENUM_NAME.BILL_STATUS_NAME[status] || '--';
    },

    getStatusClass(status) {
      switch (status) {
        case WARRANTY_BILL_STATUS.NEW:
          return 'badge badge-warning text-white';
        case WARRANTY_BILL_STATUS.PROCESSING:
          return 'badge badge-primary text-white';
        case WARRANTY_BILL_STATUS.DONE:
          return 'badge badge-success';
        case WARRANTY_BILL_STATUS.TRANSFER_STOCK:
          return 'badge badge-warning';
        default:
          return '';
      }
    },

    onClickBill(bill) {
      if (bill.isCurrentBill) return;

      if (bill.type === BILL_TYPE.WARRANTY) {
        const { href } = this.$router.resolve({
          name: 'add-warranty-bill-v2',
          query: {
            billId: bill.billNumber,
            action: 'update',
          },
        });
        window.open(href, '_blank');
      } else {
        const { href } = this.$router.resolve({
          name: 'update-bill',
          query: { id: bill.billNumber },
        });
        window.open(href, '_blank');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.bill-warranty-history {
  padding-top: 20px;

  .text-warranty-history {
    font-size: 1.08rem;
    color: brown;
    font-weight: 600;
    margin-bottom: 16px;
  }
}

.timeline-wrapper {
  display: flex;
  flex-direction: column;
  padding: 0 8px;
}

.timeline-item {
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-bottom: 0;
}

.timeline-dot-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-top: 16px;
}

.timeline-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 3px solid #d1d5db;
  background: #fff;
  flex-shrink: 0;
  z-index: 1;
  transition: border-color 0.4s ease, background 0.4s ease;

  &.dot-default {
    border-color: #1565c0;
    background: #1565c0;
  }

  &.dot-active {
    border-color: #d1d5db;
    background: #fff;
  }

  &.dot-inactive {
    border-color: #d1d5db;
    background: #fff;
  }
}

.timeline-line {
  width: 2px;
  flex: 1;
  min-height: 16px;
  background: #d1d5db;
  margin-top: 2px;
  margin-bottom: 2px;
}

/* Card */
.timeline-card {
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: border-color 0.4s ease, background 0.4s ease, box-shadow 0.4s ease;

  &.card-default {
    border-color: #1565c0;
    background: #f0f4ff;
    box-shadow: 0 2px 8px rgba(21, 101, 192, 0.15);
  }

  &.card-current {
    border-color: #e5e7eb;
    background: #fff;
    cursor: default;
  }
}

.timeline-item:hover {
  .timeline-card {
    border-color: #1565c0;
    background: #f0f4ff;
    box-shadow: 0 2px 8px rgba(21, 101, 192, 0.15);
  }

  .timeline-dot {
    border-color: #1565c0;
    background: #1565c0;

    &.dot-active,
    &.dot-default {
      background: #1565c0;
    }
  }
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.card-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.bill-type-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  color: #181c32;
}

.bill-type-short {
  font-weight: 700;
}

.bill-number {
  color: #181c32;
}

.badge-current {
  font-size: 0.78rem;
  padding: 2px 10px;
  border-radius: 12px;
  background: #1565c0;
  color: #fff;
  font-weight: 500;
}

.card-meta {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;

  .meta-separator {
    color: #d1d5db;
  }
}

.card-reason {
  font-size: 0.85rem;
  color: #c0392b;
  font-weight: 500;
  margin-top: 6px;
}
</style>
